import * as Sentry from '@sentry/react'
import type { Event, EventHint } from '@sentry/types'
import { API } from 'aws-amplify'

const hasStatisticsConsent = () =>
  window.Cookiebot?.consent?.statistics === true

/**
 * Drops the whole user context — email, name, account ids, impersonator — so
 * nothing identifying leaves the browser before the visitor has accepted
 * statistics cookies.
 *
 * The connection IP and the city Sentry derives from it are NOT controllable
 * here: Relay backfills them server-side whenever the field is absent, and an
 * explicit `null` deserialises to exactly the same thing, so it is ignored.
 * Verified empirically — a probe sent with `ip_address: null` still stored the
 * full address and city. Those two are stripped at the project level instead,
 * for every event, which is why nothing is set for them here.
 */
const anonymise = (event: Event): Event => {
  delete event.user

  return event
}

/**
 * Sentry beforeSend handler to filter out cancelled requests and add extra data
 */
export function handleSentryBeforeSend(
  event: Event,
  hint: EventHint
): Event | null {
  if (API.isCancel(hint?.originalException)) {
    return null
  }

  // Add version as extra data
  event.extra = {
    ...event.extra,
    appVersion: process.env.REACT_APP_VERSION,
  }

  if (!hasStatisticsConsent()) {
    return anonymise(event)
  }

  // Consented: the user context set at sign-in (email, name, impersonator)
  // travels as-is, so an issue can be traced back to the account that hit it.
  return event
}

/**
 * Transactions never pass through beforeSend — the SDK spec restricts that hook
 * to error events — so they need the same guard applied separately.
 */
export function handleSentryBeforeSendTransaction(event: Event): Event | null {
  if (!hasStatisticsConsent()) {
    return anonymise(event)
  }

  return event
}

// Session Replay removed for legal/privacy review (issue #358).
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DNS,
  environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
  release: `${process.env.REACT_APP_VERSION}-${process.env.REACT_APP_SENTRY_ENVIRONMENT}`,
  enabled: (process.env.REACT_APP_SENTRY_ENVIRONMENT ?? 'local') !== 'local',
  // Release Health sends its own envelopes through neither beforeSend nor
  // beforeSendTransaction, and this SDK major exposes no hook for them, so the
  // only way to keep them from carrying an inferred IP is not to send them.
  autoSessionTracking: false,
  beforeSend: handleSentryBeforeSend,
  beforeSendTransaction: handleSentryBeforeSendTransaction,
  ignoreErrors: ['Non-Error promise rejection captured'],
})
